import { Stack, Typography } from '@mui/material';
import { WrapperCardWithTitle } from '../../layouts/WrapperCardWithTitle';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import BeltNoDataAvailable from './BeltNoDataAvailable';
import { useTranslation } from 'react-i18next';
import { categoricalColors, sequentialColors } from '../../utils/chartColors';

export type BeltCategoryChartConfig =
  | { type: 'bar'; category: string; xAxisLabels: string[]; seriesData: number[] }
  | { type: 'pie'; category: string; data: { label: string; value: number }[] };

interface BeltSummaryProps {
  chartConfigs: BeltCategoryChartConfig[];
}

export default function BeltSummary({ chartConfigs }: BeltSummaryProps) {
  const { t } = useTranslation();

  return (
    <WrapperCardWithTitle title={t('belt.summary')} sx={{ height: 'max-content', minHeight: '450px' }}>
      {chartConfigs.length === 0 ? (
        <BeltNoDataAvailable />
      ) : (
        <Stack flexDirection='row' flexWrap='wrap' gap={2}>
          {chartConfigs.map((config) => (
            <WrapperCardWithTitle
              key={config.category}
              title={`belt.categories.${config.category}`}
              titleSx={{ fontSize: '0.95rem', fontWeight: 500, color: 'text.secondary' }}
              elevation={0}
              sx={{ flex: '1 1 420px', maxWidth: 500, height: 400 }}
            >
              {(() => {
                switch (config.type) {
                  case 'bar': {
                    const barColors = sequentialColors(config.xAxisLabels.length);
                    return (
                      <BarChart
                        hideLegend
                        slotProps={{ tooltip: { trigger: 'item' } }}
                        xAxis={[
                          {
                            scaleType: 'band',
                            data: config.xAxisLabels,
                            height: 80,
                          },
                        ]}
                        series={config.xAxisLabels.map((label, idx) => ({
                          label,
                          data: config.xAxisLabels.map((_, i) => (i === idx ? config.seriesData[idx] : null)),
                          color: barColors[idx],
                          stack: 'total',
                        }))}
                      />
                    );
                  }
                  case 'pie': {
                    const sliceColors = categoricalColors(config.data.length);
                    return (
                      <PieChart
                        series={[{ data: config.data.map((d, i) => ({ id: i, ...d, color: sliceColors[i] })) }]}
                      />
                    );
                  }
                  default:
                    return <Typography color='text.secondary'>{t('belt.unknownDataFormat')}</Typography>;
                }
              })()}
            </WrapperCardWithTitle>
          ))}
        </Stack>
      )}
    </WrapperCardWithTitle>
  );
}
